// features/subscription/SubscriptionDashboard.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Tab } from '@headlessui/react';
import SubscriptionPlans from './SubscriptionPlans';
import UserSubscriptions from './UserSubscriptions';
import CreditBalance from './CreditBalance';
import { useSubscription } from '../../context/SubscriptionContext';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const SubscriptionDashboard = () => {
  const { t } = useTranslation();
  const {
    activeSubscription,
    creditBalance,
    refreshSubscription,
    refreshCreditBalance
  } = useSubscription();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Refresh data when component mounts - only once
  useEffect(() => {
    // Skip refresh if we're already refreshing
    if (isRefreshing) return;
    
    const refreshData = async () => {
      try {
        setIsRefreshing(true);
        console.log('Refreshing subscription dashboard data');
        await Promise.all([
          refreshSubscription(),
          refreshCreditBalance()
        ]);
      } catch (error) {
        console.error('Error refreshing dashboard data:', error);
      } finally {
        setIsRefreshing(false);
      }
    };

    refreshData();
    // Empty dependency array ensures this only runs once when component mounts
  }, []);

  const tabs = [
    { name: t('subscription.mySubscriptions'), component: <UserSubscriptions /> },
    { name: t('subscription.plans'), component: <SubscriptionPlans /> },
    { name: t('subscription.credits'), component: <CreditBalance /> },
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2 text-center dark:text-white">{t('subscription.dashboard')}</h1>
        <p className="text-gray-600 dark:text-gray-400">{t('subscription.dashboardDescription')}</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-lg text-gray-600 dark:text-gray-400 mb-2">{t('subscription.subscription')}</h2>
          <div className="text-2xl font-bold">
            {activeSubscription ? activeSubscription.plan_name : t('subscription.none')}
          </div>
          {activeSubscription && (
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('subscription.validUntil')}: {new Date(activeSubscription.end_date).toLocaleDateString()}
            </div>
          )}
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-lg text-gray-600 dark:text-gray-400 mb-2">{t('subscription.availableCredits')}</h2>
          <div className="text-2xl font-bold">{creditBalance}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('subscription.creditsDescription')}
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-lg text-gray-600 dark:text-gray-400 mb-2">{t('subscription.nextPayment')}</h2>
          <div className="text-2xl font-bold">
            {activeSubscription && activeSubscription.auto_renew
              ? new Date(activeSubscription.end_date).toLocaleDateString()
              : t('subscription.none')}
          </div>
          {activeSubscription && activeSubscription.auto_renew && (
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              ${parseFloat(activeSubscription.price || 0).toFixed(2)} / {t('subscription.month')}
            </div>
          )}
        </div>
      </div>
      
      <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
        <Tab.List className="flex p-1 space-x-1 bg-gray-100 dark:bg-gray-800 rounded-xl mb-8">
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              className={({ selected }) =>
                classNames(
                  'w-full py-3 text-sm leading-5 font-medium rounded-lg',
                  'focus:outline-none focus:ring-2 ring-offset-2 ring-offset-primary ring-white ring-opacity-60',
                  selected
                    ? 'bg-white dark:bg-gray-700 shadow text-primary-600 dark:text-primary'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-white/[0.12] hover:text-primary-600 dark:hover:text-primary'
                )
              }
            >
              {tab.name}
            </Tab>
          ))}
        </Tab.List>
        <Tab.Panels className="mt-2">
          {tabs.map((tab, index) => (
            <Tab.Panel
              key={index}
              className={classNames(
                'rounded-xl bg-white dark:bg-gray-800 p-3',
                'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none'
              )}
            >
              {tab.component}
            </Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
};

export default SubscriptionDashboard;
